import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MapPin, IndianRupee, Maximize2, ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const FeaturedCarousel = () => {
  const [properties, setProperties] = useState([]);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/properties`)
      .then((res) => {
        // Top 5 properties (newest)
        const featured = (res.data || []).filter((p) => p.status === 'Available').slice(0, 5);
        setProperties(featured);
      })
      .catch(() => setProperties([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (properties.length <= 1) return;
    const id = setInterval(() => setActive((a) => (a + 1) % properties.length), 5500);
    return () => clearInterval(id);
  }, [properties.length]);

  const scrollToProjects = () => {
    document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' });
  };

  if (loading || properties.length === 0) return null;

  const prev = () => setActive((a) => (a - 1 + properties.length) % properties.length);
  const next = () => setActive((a) => (a + 1) % properties.length);
  const current = properties[active];
  const imgSrc = current.image?.match(/^\/api\/(uploads|files)\//)
    ? `${BACKEND_URL}${current.image}`
    : current.image;

  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Section title */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <div className="inline-flex items-center gap-2 text-emerald-700 text-xs font-bold tracking-[0.18em] uppercase mb-2">
              <Star size={14} className="fill-emerald-700 text-emerald-700" />
              Featured Listings
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
              Handpicked <span className="text-emerald-700">for you</span>
            </h2>
          </div>
          {properties.length > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={prev}
                className="w-10 h-10 rounded-full bg-white border border-slate-300 hover:border-emerald-700 hover:text-emerald-700 text-slate-700 flex items-center justify-center transition-colors"
                aria-label="Previous"
                data-testid="featured-prev"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={next}
                className="w-10 h-10 rounded-full bg-white border border-slate-300 hover:border-emerald-700 hover:text-emerald-700 text-slate-700 flex items-center justify-center transition-colors"
                aria-label="Next"
                data-testid="featured-next"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </div>

        {/* Banner */}
        <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-slate-900 min-h-[380px] md:min-h-[420px]">
          <img
            key={current.id}
            src={imgSrc}
            alt={current.title}
            onError={(e) => {
              e.target.src = 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1600&q=80';
            }}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-slate-900/95 via-slate-900/65 to-slate-900/15"></div>

          {/* Content */}
          <div className="relative z-10 grid md:grid-cols-2 items-end md:items-center p-8 md:p-12 min-h-[380px] md:min-h-[420px]">
            <div className="text-white">
              <div className="inline-block px-3 py-1 bg-emerald-700 text-white text-xs font-bold tracking-wide rounded-full mb-4">
                FEATURED · {current.type}
              </div>
              <h3 className="text-3xl md:text-4xl font-bold mb-3 leading-tight">{current.title}</h3>
              <div className="flex items-center text-slate-200 mb-5">
                <MapPin size={16} className="mr-2 text-emerald-300" />
                <span>{current.location}</span>
              </div>
              <div className="flex flex-wrap gap-6 mb-7">
                <div>
                  <div className="text-xs text-slate-300 uppercase tracking-wider mb-1">Price</div>
                  <div className="flex items-center text-2xl md:text-3xl font-bold text-emerald-300">
                    <IndianRupee size={22} className="mr-1" />
                    {current.price.replace('₹', '')}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-300 uppercase tracking-wider mb-1">Area</div>
                  <div className="flex items-center text-lg md:text-xl font-semibold text-white">
                    <Maximize2 size={16} className="mr-2 text-slate-300" />
                    {current.area}
                  </div>
                </div>
              </div>
              <Button
                onClick={scrollToProjects}
                className="btn-am-primary text-base px-7 py-4"
                data-testid="featured-explore-btn"
              >
                Explore All Properties
              </Button>
            </div>
            {/* Decorative description card */}
            <div className="hidden md:block">
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 max-w-md ml-auto">
                <p className="text-white/85 text-sm leading-relaxed line-clamp-5">
                  {current.description?.slice(0, 240) ||
                    'A premium Acre Mitra listing — verified, ready to view, and waiting for the right owner.'}
                  {current.description && current.description.length > 240 ? '…' : ''}
                </p>
              </div>
            </div>
          </div>

          {/* Dot indicators */}
          {properties.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-1.5">
              {properties.map((p, i) => (
                <button
                  key={p.id || `dot-${i}`}
                  onClick={() => setActive(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === active ? 'w-7 bg-emerald-400' : 'w-1.5 bg-white/40 hover:bg-white/70'
                  }`}
                  aria-label={`Show slide ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default FeaturedCarousel;
